import { useAuthStore } from "@persisted-state/auth/auth.store";
import type {
  DeficiencyStatus,
  DeficiencyTask,
  DeficiencyTaskStatus,
} from "@services/deficiencies/types";

export type DeficiencyFlowData = {
  id?: string;
  status?: DeficiencyStatus | null;
  task?: DeficiencyTask | null;
};

const SHIPPING_AGENT_ROLE = "shipping-agent";
const RESOLVABLE_STATUSES: DeficiencyStatus[] = ["PENDING_RESOLUTION"];
const AWAITING_APPROVAL_STATUSES: DeficiencyStatus[] = ["AWAITING_APPROVAL"];

export const DEFICIENCY_TASK_STATUSES = {
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  TERMINATED: "TERMINATED",
  UPDATED: "UPDATED",
  UNCLAIMED: "UNCLAIMED",
} as const;

const isTaskNotClaimed = (taskStatus: DeficiencyTaskStatus | null | undefined) =>
  taskStatus === DEFICIENCY_TASK_STATUSES.READY;

const isTaskClaimed = (taskStatus: DeficiencyTaskStatus | null | undefined) =>
  taskStatus === DEFICIENCY_TASK_STATUSES.CLAIMED;

const isTaskOwner = (claimOwnerName: string | null | undefined) => {
  const user = useAuthStore.getState().user;

  if (!claimOwnerName) {
    return false;
  }

  return claimOwnerName === user?.email || claimOwnerName === user?.preferredUsername;
};

const isUserClaimedTaskOwner = (
  taskStatus: DeficiencyTaskStatus | null | undefined,
  claimOwnerName: string | null | undefined,
) => isTaskClaimed(taskStatus) && isTaskOwner(claimOwnerName);

const isAwaitingApproval = (status: DeficiencyStatus | null | undefined) =>
  !!status && AWAITING_APPROVAL_STATUSES.includes(status);

export const canResolveDeficiency = (
  status: DeficiencyStatus | null | undefined,
  userRoles: string[] = [],
) => {
  return (
    userRoles.includes(SHIPPING_AGENT_ROLE) && !!status && RESOLVABLE_STATUSES.includes(status)
  );
};

export const canUserClaim = (
  taskStatus: DeficiencyTaskStatus | null | undefined,
  status: DeficiencyStatus | null | undefined,
) => isTaskNotClaimed(taskStatus) && isAwaitingApproval(status);

export const canUserUnclaim = (
  taskStatus: DeficiencyTaskStatus | null | undefined,
  claimOwnerName: string | null | undefined,
  status: DeficiencyStatus | null | undefined,
) => isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isAwaitingApproval(status);

export const canUserApprove = (
  taskStatus: DeficiencyTaskStatus | null | undefined,
  claimOwnerName: string | null | undefined,
  status: DeficiencyStatus | null | undefined,
) => isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isAwaitingApproval(status);

export const canUserReject = (
  taskStatus: DeficiencyTaskStatus | null | undefined,
  claimOwnerName: string | null | undefined,
  status: DeficiencyStatus | null | undefined,
) => isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isAwaitingApproval(status);
